// The player's record, in this browser: every round played, today's paper's result, the streak and
// how good their instinct is by kind of news. A convenience kept per viewer: losing it loses
// nothing but the history.

import { createStore, useStore } from "../state/store";
import { dollars } from "../../shared/game/money.ts";

export type { Side } from "../../shared/game/rounds.ts";
import type { Side } from "../../shared/game/rounds.ts";

export type Play = {
  round: string;
  deck: "today" | "fresh" | "survival";
  /** Today's paper's number, for today's deck. */
  day: number | null;
  side: Side | null;
  leverage: number;
  /** The path point closed at, null if held to the end (the decision, as the board scores it). */
  closedAt?: number | null;
  /** Cents, as a decimal string (BigInt does not survive JSON). */
  pnl: string;
  busted: boolean;
  category: string;
  at: number;
};

const KEY = "newspaper.hl:plays:1";
const KEEP = 1_000;

function read(): Play[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(parsed) ? (parsed as Play[]).filter((play) => typeof play?.round === "string" && typeof play.pnl === "string") : [];
  } catch {
    return [];
  }
}

export const plays = createStore<Play[]>(read());
export const usePlays = () => useStore(plays);

export function record(play: Play) {
  plays.set((all) => [...all, play].slice(-KEEP));
  try {
    localStorage.setItem(KEY, JSON.stringify(plays.get()));
  } catch {
    // kept for this tab
  }
}

export { MARGIN_CENTS, START_CENTS } from "../../shared/game/rounds.ts";
import { START_CENTS } from "../../shared/game/rounds.ts";

/** A deck's plays, in order, and the bankroll after them. */
export function deckResult(all: Play[], day: number): { plays: Play[]; bankroll: bigint } | null {
  const today = all.filter((play) => play.deck === "today" && play.day === day);
  if (!today.length) return null;
  const bankroll = today.reduce((sum, play) => sum + BigInt(play.pnl), START_CENTS);
  return { plays: today, bankroll };
}

/** Days in a row with today's paper finished, counting back from today (or yesterday). */
export function streakOf(all: Play[], day: number, size: number): number {
  const finished = new Set<number>();
  const counts = new Map<number, number>();
  for (const play of all) {
    if (play.deck !== "today" || play.day === null) continue;
    counts.set(play.day, (counts.get(play.day) ?? 0) + 1);
    if ((counts.get(play.day) ?? 0) >= size) finished.add(play.day);
  }
  let streak = 0;
  for (let d = finished.has(day) ? day : day - 1; finished.has(d); d--) streak++;
  return streak;
}

/** How often each kind of news was read right: trades that made money, of trades taken. */
export function instinctOf(all: Play[]): { category: string; right: number; taken: number }[] {
  const by = new Map<string, { right: number; taken: number }>();
  for (const play of all) {
    if (!play.side) continue;
    const held = by.get(play.category) ?? { right: 0, taken: 0 };
    held.taken += 1;
    if (BigInt(play.pnl) > 0n) held.right += 1;
    by.set(play.category, held);
  }
  return [...by.entries()].map(([category, counts]) => ({ category, ...counts })).sort((a, b) => b.taken - a.taken);
}

/** 🟩 a win, 🟥 a loss, 💀 busted, ⬜ sat out. */
export const markOf = (play: Play) => (!play.side ? "⬜" : play.busted ? "💀" : BigInt(play.pnl) > 0n ? "🟩" : "🟥");

export function shareText(day: number, result: { plays: Play[]; bankroll: bigint }, standing: string | null = null): string {
  const lines = [`newspaper.hl #${day}`, `${dollars(START_CENTS)} → ${dollars(result.bankroll)}`, result.plays.map(markOf).join("")];
  if (standing) lines.push(standing);
  lines.push("newspaper-hl.pages.dev");
  return lines.join("\n");
}

// ---------------------------------------------------------------- survival

const BEST = "newspaper.hl:survival:1";

export type Run = { bankroll: string; rounds: number; at: number };

function readBest(): Run | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(BEST) ?? "null") as Run | null;
    return parsed && typeof parsed.bankroll === "string" && typeof parsed.rounds === "number" ? parsed : null;
  } catch {
    return null;
  }
}

export const bestRun = createStore<Run | null>(readBest());
export const useBestRun = () => useStore(bestRun);

/** Keeps the run if it beat the best: more money walked away with. */
export function finishRun(run: Run): boolean {
  const held = bestRun.get();
  if (held && BigInt(held.bankroll) >= BigInt(run.bankroll)) return false;
  bestRun.set(run);
  try {
    localStorage.setItem(BEST, JSON.stringify(run));
  } catch {
    // kept for this tab
  }
  return true;
}
